import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { cn } from '../../../utils/class-utils';

export type ImageContainerSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';
export type ImageContainerAspectRatio = 'square' | 'landscape' | 'portrait' | '16:9' | '4:3';

@Component({
    selector: 'jjames-image-container',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './image-container.component.html',
    styleUrls: ['./image-container.component.scss']
})
export class JJamesImageContainerComponent {
    @Input() imageSrc?: string;
    @Input() altText?: string;
    @Input() size: ImageContainerSize = 'md';
    @Input() aspectRatio: ImageContainerAspectRatio = 'landscape';
    @Input() rounded = true;
    @Input() border = true;
    @Input() overlay = false;
    @Input() placeholderText?: string;
    @Input() showPlaceholderIcon = true;

    get containerClasses(): string {
        const baseClasses = 'relative overflow-hidden bg-gray-50';
        const sizeClasses = this.getSizeClasses();
        const aspectClasses = this.getAspectRatioClasses();
        const borderClasses = this.border ? 'border border-gray-200' : '';
        const roundedClasses = this.rounded ? 'rounded-lg' : '';

        return cn(baseClasses, sizeClasses, aspectClasses, borderClasses, roundedClasses);
    }

    get imageClasses(): string {
        return 'w-full h-full object-cover';
    }

    get placeholderClasses(): string {
        return 'w-full h-full flex flex-col items-center justify-center text-gray-400';
    }

    get overlayClasses(): string {
        return 'absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-200';
    }

    private getSizeClasses(): string {
        const sizeMap: Record<ImageContainerSize, string> = {
            sm: 'w-16 h-12',
            md: 'w-24 h-18',
            lg: 'w-32 h-24',
            xl: 'w-48 h-36',
            full: 'w-full'
        };
        return sizeMap[this.size];
    }

    private getAspectRatioClasses(): string {
        const aspectMap: Record<ImageContainerAspectRatio, string> = {
            square: 'aspect-square',
            landscape: 'aspect-video',
            portrait: 'aspect-[3/4]',
            '16:9': 'aspect-video',
            '4:3': 'aspect-[4/3]'
        };
        return aspectMap[this.aspectRatio];
    }

    onImageError(): void {
        console.warn('Failed to load image:', this.imageSrc);
        // Could emit an event or handle error state here
    }

    onImageLoad(): void {
        // Could emit an event when image loads successfully
    }
}